// Tri deterministe et stable du fishbone (branches + sous-arêtes).
//
// P1 — ordre stable : l'ordre reseau (DynamoDB) n'est pas garanti, ce qui
// faisait « bouger » l'affichage a chaque refetch. On impose un ordre stable.
// P2 (#1) — ordre MANUEL persistant : si au moins un noeud porte une `position`
// (Int stampe a la creation, reordonnable via fleches), on trie par position
// croissante ; les noeuds sans position vont en fin, departages par createdAt
// puis id (deux champs immuables) pour un rendu toujours identique.

type SortableNode = {
	id?: string;
	name?: string | null;
	startOn?: string | null;
	createdAt?: string | null;
	position?: number | null;
};

// Departage final, toujours deterministe : createdAt (ordre de creation) puis id.
const tieBreak = (a: SortableNode, b: SortableNode): number => {
	const ca = a.createdAt || '';
	const cb = b.createdAt || '';
	if (ca !== cb) return ca < cb ? -1 : 1;
	const ia = a.id || '';
	const ib = b.id || '';
	if (ia === ib) return 0;
	return ia < ib ? -1 : 1;
};

/**
 * Tri canonique des noeuds fishbone (branches OU items). Renvoie une COPIE.
 * Par `position` quand presente, fallback stable createdAt -> id.
 */
export const sortFishboneNodes = <T extends SortableNode>(nodes: T[] = []): T[] => {
	const hasPosition = nodes.some(
		(n) => n != null && n.position !== null && n.position !== undefined
	);
	if (!hasPosition) {
		return [...(nodes || [])].sort(tieBreak);
	}
	return [...(nodes || [])].sort((a, b) => {
		const pa = a?.position;
		const pb = b?.position;
		const aHas = pa !== null && pa !== undefined;
		const bHas = pb !== null && pb !== undefined;
		// Noeuds sans position rejetes en fin.
		if (aHas && !bHas) return -1;
		if (!aHas && bHas) return 1;
		if (aHas && bHas && pa !== pb) return (pa as number) - (pb as number);
		return tieBreak(a, b);
	});
};

// Sous-arêtes : position-aware (respecte la reorganisation manuelle), fallback stable.
export const sortFishboneItems = <T extends SortableNode>(items: T[]): T[] =>
	sortFishboneNodes(items);

// Arêtes : respecte la preference utilisateur ('name' | 'startOn'), avec departage
// createdAt puis id. Le mode 'manual' (ordre par position) est gere par l'appelant
// via sortFishboneNodes.
export const sortFishboneBranches = <T extends SortableNode>(
	branches: T[],
	branchSort: string = 'startOn'
): T[] => {
	return [...(branches || [])].sort((a, b) => {
		if (branchSort === 'name') {
			const na = (a.name || '').toLowerCase();
			const nb = (b.name || '').toLowerCase();
			if (na !== nb) return na < nb ? -1 : 1;
		} else {
			const sa = a.startOn || '';
			const sb = b.startOn || '';
			if (sa !== sb) return sa < sb ? -1 : 1;
			const na = (a.name || '').toLowerCase();
			const nb = (b.name || '').toLowerCase();
			if (na !== nb) return na < nb ? -1 : 1;
		}
		return tieBreak(a, b);
	});
};
